/**
 * DOCX Serializer for TipTap/ProseMirror documents.
 *
 * Converts a TipTap document (or fragment) to a docx Document object
 * using the `docx` npm package. The output can be packed into a .docx
 * file via `Packer.toBlob()`.
 *
 * Style-driven: reads formatting from the transcriptStyles config so
 * that future style customization automatically flows through to export.
 */

import { Document, Paragraph, TextRun } from 'docx';
import transcriptStyles from '../config/transcriptStyles';

/**
 * Extract the first font name from a CSS font-family string.
 * "'Courier New', Courier, monospace" => "Courier New"
 */
function extractFontName(fontFamily) {
  const first = fontFamily.split(',')[0].trim();
  return first.replace(/^['"]|['"]$/g, '');
}

/**
 * Parse a CSS font size string to half-points for docx.
 * "10pt" => 20, "12pt" => 24
 */
function parseFontSizeToHalfPoints(fontSize) {
  const pt = parseFloat(fontSize);
  return isNaN(pt) ? 20 : pt * 2;
}

/**
 * Apply CSS-style properties from the config to a docx TextRun options object.
 * This is the generic mapping layer that keeps the serializer config-driven.
 */
function applyStyleToRunOptions(runOptions, styleObj) {
  if (!styleObj) return;

  if (styleObj.fontWeight === 'bold') {
    runOptions.bold = true;
  }
  if (styleObj.fontStyle === 'italic') {
    runOptions.italics = true;
  }
  if (styleObj.color) {
    runOptions.color = styleObj.color.replace('#', '');
  }
  if (styleObj.textDecoration === 'underline') {
    runOptions.underline = { type: 'single' };
  }
  if (styleObj.textDecoration === 'line-through') {
    runOptions.strike = true;
  }
}

/**
 * Serialize a TipTap/ProseMirror document or fragment to a docx Document.
 *
 * @param {Node|Fragment} docOrFragment - ProseMirror document or fragment
 * @param {Object} [config] - Style config (defaults to transcriptStyles)
 * @returns {Document} docx Document instance ready for Packer.toBlob()
 */
export default function serializeToDOCX(docOrFragment, config = transcriptStyles) {
  const font = extractFontName(config.base.fontFamily);
  const size = parseFontSizeToHalfPoints(config.base.fontSize);
  const paragraphs = [];

  docOrFragment.forEach((node) => {
    if (node.type.name === 'paragraph') {
      const lineType = node.attrs.lineType || 'default';
      const lineTypeStyle = config.lineTypes[lineType] || {};

      const textRuns = [];

      node.forEach((child) => {
        if (child.isText) {
          const runOptions = {
            text: child.text,
            font,
            size,
          };

          // Apply paragraph-level styles (e.g., bold for speech, gray for embodied)
          applyStyleToRunOptions(runOptions, lineTypeStyle);

          // Apply inline mark styles
          if (child.marks) {
            child.marks.forEach((mark) => {
              const markStyle = config.marks[mark.type.name];
              applyStyleToRunOptions(runOptions, markStyle);
            });
          }

          textRuns.push(new TextRun(runOptions));
        } else if (child.type.name === 'hardBreak') {
          textRuns.push(new TextRun({ break: 1 }));
        }
      });

      // Empty paragraphs need an empty TextRun to preserve spacing
      if (textRuns.length === 0) {
        textRuns.push(new TextRun({ text: '', font, size }));
      }

      paragraphs.push(
        new Paragraph({
          children: textRuns,
          spacing: {
            line: 276,   // 1.15 line spacing (240 twips * 1.15)
            before: 0,
            after: 0,
          },
        })
      );
    }
  });

  return new Document({
    sections: [
      {
        children: paragraphs,
      },
    ],
  });
}
